import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { currentAddPatronsSlotIdAtom } from "@/features/AddPatrons/atoms/stores";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { AddPatrons } from "../../../../../../../features/AddPatrons";
import { AddPeopleSearch_Atom } from "../../../../../../../features/AddPatrons/atoms/stores";
import { Patron } from "../../../../../../../features/AddPatrons/types";
import { getBookingPlayerNoun } from "../../../BookingSummary/utils/getBookingPlayerNoun";
import { addNewRoundAtom } from "../../atoms/actions/AddNewRoundAtom/addNewRoundAtom";
import { enterEditableRoundsStepAtom } from "../../atoms/actions/EnterEditableRoundsStepAtom/enterEditableRoundsStepAtom";
import {
  cancelRequestGroupAtom,
  finishRequestGroupAtom,
} from "../../atoms/actions/editRequestGroups/editRequestGroups";
import { removeRoundByPlayerIdAtom } from "../../atoms/actions/removeRoundByPlayerIdAtom/removeRoundByPlayerIdAtom";
import { replacePlayerOnRoundAtom } from "../../atoms/actions/replacePlayerOnRoundAtom/replacePlayerOnRoundAtom";
import {
  currentBookingAtom,
  currentModalStepAtom,
  defaultMembershipTypeIdAtom,
  pendingRequestGroupAtom,
} from "../../atoms/stores";
import { PatronEditBooking } from "../../types";
import { AddPlayerStep_Booking$key } from "./__generated__/AddPlayerStep_Booking.graphql";
import { AddNewPlayerTile } from "./components/AddNewPlayerTile/AddNewPlayerTile";

type Props = {
  bookingRef: AddPlayerStep_Booking$key;
  currentSessionUserClientId: string;
};
export const AddPlayerStep = ({
  bookingRef,
  currentSessionUserClientId,
}: Props) => {
  const booking = useFragment(AddPlayerStepFragmentString, bookingRef);
  const startSlotId = booking.startSlot?.id ?? "";
  const currentBooking = useAtomValue(currentBookingAtom);
  const currentModalStep = useAtomValue(currentModalStepAtom);
  const pendingGroup = useAtomValue(pendingRequestGroupAtom);
  const cancelRequestGroup = useSetAtom(cancelRequestGroupAtom);
  const finishRequestGroup = useSetAtom(finishRequestGroupAtom);
  const isNewGroupFull =
    !!pendingGroup &&
    (currentBooking?.rounds.filter(
      (round) =>
        round.bookingIndex === pendingGroup.bookingIndex &&
        !round.isNonPlayingHost
    ).length ?? 0) >= (currentBooking?.groupCapacity ?? 4);
  const defaultMembershipTypeId = useAtomValue(defaultMembershipTypeIdAtom);
  const [search, setSearch] = useAtom(AddPeopleSearch_Atom);
  const setCurrentSlotId = useSetAtom(currentAddPatronsSlotIdAtom);
  const selectedBookingIndex = useMemo(() => {
    if (
      currentModalStep.type === "AddPlayer" ||
      currentModalStep.type === "AddPlayerPrivate"
    ) {
      return currentModalStep.bookingIndex ?? 0;
    }

    if (currentModalStep.type === "ReplacePlayer") {
      return (
        currentBooking?.rounds.find(
          (round) => round.localId === currentModalStep.roundId
        )?.bookingIndex ?? 0
      );
    }

    return null;
  }, [currentBooking?.rounds, currentModalStep]);
  const selectedBookingRounds = useMemo(
    () =>
      currentBooking?.rounds.filter(
        (round) =>
          selectedBookingIndex == null ||
          (round.bookingIndex ?? 0) === selectedBookingIndex
      ) ?? [],
    [currentBooking?.rounds, selectedBookingIndex]
  );
  const selectedPlayerIds = useMemo(() => {
    const bookingPlayerIds = selectedBookingRounds.flatMap((round) =>
      round.player.id ? [round.player.id] : []
    );

    return Array.from(new Set(bookingPlayerIds));
  }, [selectedBookingRounds]);
  const enterEditableRoundsStep = useSetAtom(enterEditableRoundsStepAtom);
  const removeRoundByPlayerId = useSetAtom(removeRoundByPlayerIdAtom);

  useEffect(() => {
    if (startSlotId) {
      setCurrentSlotId(startSlotId);
    }
  }, [setCurrentSlotId, startSlotId]);

  const replacingRound =
    currentModalStep.type === "ReplacePlayer"
      ? selectedBookingRounds.find(
          (round) => round.localId === currentModalStep.roundId
        )
      : null;
  const replacingPlayer = replacingRound?.player;
  const playerNoun = getBookingPlayerNoun({
    bookingType: currentBooking?.type,
    copyOverrides: currentBooking?.copyOverrides,
  });
  const replacingPlayerName =
    replacingPlayer?.clientType === "customer" &&
    replacingPlayer?.firstName &&
    replacingPlayer?.lastName
      ? `${replacingPlayer.firstName} ${replacingPlayer.lastName}`
      : t`New ${playerNoun.singular}`;

  const panelHeadertitle = pendingGroup
    ? t`Add Group`
    : currentModalStep.type === "ReplacePlayer"
    ? t`Replace ${replacingPlayerName}`
    : t`Add ${playerNoun.singular}`;

  const addNewRound = useSetAtom(addNewRoundAtom);
  const replacePlayer = useSetAtom(replacePlayerOnRoundAtom);
  const handleAddPress = useCallback(
    (player: PatronEditBooking.RoundPlayer) => {
      if (!!replacingRound) {
        replacePlayer({ roundId: replacingRound.localId, player });
      } else {
        addNewRound({
          player,
          bookingIndex:
            currentModalStep.type === "AddPlayer"
              ? currentModalStep.bookingIndex
              : undefined,
        });
      }
      if (!pendingGroup) enterEditableRoundsStep();
    },
    [
      addNewRound,
      currentModalStep,
      enterEditableRoundsStep,
      replacePlayer,
      replacingRound,
      pendingGroup,
    ]
  );

  const currentPatron = useMemo<Patron | undefined>(() => {
    const currentRound = selectedBookingRounds.find(
      (round) => round.player.id === currentSessionUserClientId
    );

    if (!currentSessionUserClientId || !currentRound) return undefined;

    return {
      playerId: currentSessionUserClientId,
      firstName: currentRound?.player.firstName ?? "",
      lastName: currentRound?.player.lastName ?? "",
      profileImageId: currentRound?.player.profileImagePublicId ?? "",
      membershipTypeId:
        currentRound?.player.clientType === "customer"
          ? currentRound.player.membershipTypeId
          : undefined,
      type: "Customer",
      memberGroups:
        currentRound?.player.clientType === "customer" &&
        currentRound.player.memberGroups
          ? currentRound.player.memberGroups.flatMap((group) =>
              group ? [group] : []
            )
          : [],
    };
  }, [currentSessionUserClientId, selectedBookingRounds]);

  const handleCustomerPress = useCallback(
    (player: Patron, checked: boolean) => {
      if (checked) {
        handleAddPress({
          clientType: "customer",
          id: player.playerId || player.internalIdentifier || "",
          firstName: player.firstName ?? "",
          lastName: player.lastName ?? "",
          membershipTypeId:
            player.membershipTypeId || defaultMembershipTypeId || "",
          profileImagePublicId: player.profileImageId ?? null,
          memberGroups: player.memberGroups ?? [],
        });
      } else {
        removeRoundByPlayerId(
          player.playerId || player.internalIdentifier || ""
        );
      }
    },
    [defaultMembershipTypeId, handleAddPress, removeRoundByPlayerId]
  );

  return (
    <VStack flex={1} testID="add-player-step">
      <PanelHeader
        paddingVariant="medium"
        header={panelHeadertitle}
        outerLeftComponent={
          <Button
            accessibilityLabel={t`Close`}
            testID="modal-close-btn"
            variant="secondary"
            onPress={() => {
              if (pendingGroup) cancelRequestGroup();
              else enterEditableRoundsStep();
            }}
            leftIcon={<ArrowLeftIcon />}
          />
        }
      />

      <VStack px={5} pt={6}>
        {!isNewGroupFull ? (
          <AddNewPlayerTile
            onPress={handleAddPress}
            playerNounSingular={playerNoun.singular}
          />
        ) : null}
      </VStack>
      <AddPatrons
        includeTransportationType={
          booking.startSlot?.agenda?.facility?.type === "GOLF_COURSE"
        }
        supportsPublic
        search={search}
        setSearch={setSearch}
        currentPatron={currentPatron}
        handleAddCustomerClick={handleCustomerPress}
        handleAddMemberClick={() => {}}
        handleAddGuestClick={() => {}}
        isGroupFullOfAttendees={false}
        isGroupFullOfCustomers={isNewGroupFull}
        checkNewDisabled={isNewGroupFull}
        bg="surface-primary"
        players={selectedPlayerIds}
        disabledPlayerIds={
          currentBooking?.rounds.flatMap((round) =>
            (round.bookingIndex ?? 0) !== selectedBookingIndex &&
            round.player.id
              ? [round.player.id]
              : []
          ) ?? []
        }
        allPlayers={selectedPlayerIds}
        unknownGuestCount={0}
        unknownCustomerEnabled={false}
        keepItemInListAfterCheck={!!pendingGroup}
      />
      {pendingGroup ? (
        <VStack padding={5} borderTopWidth={1} borderTopColor="outline-default">
          <Button
            accessibilityLabel={t`Continue`}
            testID="add-group-continue-button"
            isDisabled={
              !selectedBookingRounds.some((round) => !!round.player.id)
            }
            onPress={() => finishRequestGroup()}
          >
            {t`Continue`}
          </Button>
        </VStack>
      ) : null}
    </VStack>
  );
};

const AddPlayerStepFragmentString = graphql`
  fragment AddPlayerStep_Booking on Booking {
    startSlot {
      id
      agenda {
        facility {
          type
        }
      }
      club {
        id
      }
    }
  }
`;
